'use client';

import { useState } from 'react';

import { Card } from '@/components/card';
import { GRADIENT_CTA_CLASS } from '@/components/free-tools/brand';
import { cn } from '@/lib/utils';
import Link from 'next/link';

import { trackFreeToolCtaClick } from '@/components/free-tools/track-free-tool-cta';
import { useLocalToolRun } from '@/components/free-tools/local-tool-runs';
import { snippetTitle } from '@/lib/free-tools/local-tool-runs';
import {
  analyzeTranscript,
  type AnalyzeTranscriptResponse,
  type AnalyzerScore,
} from '@/app/actions/free-tools/analyze-transcript';

/**
 * Client interaction for the Transcript Quality Analyzer free tool.
 *
 * Paste -> server action -> 4-dimension score + AI summary + best-guess of
 * which tool produced the transcript. CTA below the result routes to /sign-up
 * with UTM attribution.
 */
export function TranscriptQualityAnalyzerClient() {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<AnalyzeTranscriptResponse | null>(null);
  // The text the shown result was scored on; editing after a run is not a new run.
  const [scoredText, setScoredText] = useState('');

  useLocalToolRun(
    result?.ok
      ? {
          title: snippetTitle(scoredText),
          summary: `Score ${result.score.overall}/100${result.likelyOrigin ? ` · likely ${result.likelyOrigin}` : ''}`,
          state: { text: scoredText, result },
        }
      : null,
    (state) => {
      const restoredText = typeof state.text === 'string' ? state.text : '';
      setText(restoredText);
      setScoredText(restoredText);
      // SAFETY: written by the recorder above from an ok AnalyzeTranscriptResponse.
      setResult((state.result as AnalyzeTranscriptResponse | undefined) ?? null);
    }
  );

  async function handleAnalyze() {
    setLoading(true);
    setResult(null);
    const res = await analyzeTranscript(text);
    setScoredText(text);
    setResult(res);
    setLoading(false);
  }

  return (
    <div className="rounded-[28px] border border-[rgb(var(--surface-border))] bg-[rgb(var(--surface))] p-5 sm:p-6">
      <label htmlFor="quality-transcript" className="mb-2 block text-sm font-medium">
        Transcript
      </label>
      <textarea
        id="quality-transcript"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Paste a transcript here. Anything from a few sentences up to a 10,000-character snippet works. We keep it lightweight."
        rows={8}
        className="w-full rounded-2xl border border-[rgb(var(--surface-border))] bg-[rgb(var(--background))] p-4 text-sm text-[rgb(var(--foreground))] placeholder:text-[rgb(var(--foreground-secondary))] focus:border-[rgb(var(--primary))] focus:outline-none"
      />
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={handleAnalyze}
          disabled={loading || text.trim().length < 80}
          className={cn(
            GRADIENT_CTA_CLASS,
            'px-5 py-2.5 text-sm disabled:cursor-not-allowed disabled:opacity-40'
          )}
        >
          {loading ? 'Analyzing…' : 'Analyze transcript'}
        </button>
        <p className="text-xs text-[rgb(var(--foreground-tertiary))]">
          {text.trim().length < 80
            ? 'Paste at least 80 characters to enable.'
            : `${text.trim().length.toLocaleString()} characters`}
        </p>
      </div>

      {result && !result.ok && (
        <div className="mt-6 rounded-2xl border border-[rgb(var(--error))]/40 bg-[rgb(var(--error))]/10 p-4 text-sm text-[rgb(var(--error))]">
          {result.error}
        </div>
      )}

      {result && result.ok && <ResultBlock result={result} />}
    </div>
  );
}

function ResultBlock({ result }: { result: Extract<AnalyzeTranscriptResponse, { ok: true }> }) {
  const { score, summary, issues, likelyOrigin } = result;

  return (
    <Card className="mt-8 space-y-6 p-5">
      {/* Headline overall + likely-origin badge */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-[rgb(var(--foreground-tertiary))]">
            Overall
          </p>
          <p className="text-4xl font-bold text-[rgb(var(--foreground))]">{score.overall}/100</p>
        </div>
        {likelyOrigin && (
          <div className="text-right">
            <p className="text-xs uppercase tracking-wide text-[rgb(var(--foreground-tertiary))]">
              Likely from
            </p>
            <p className="text-base font-semibold text-[rgb(var(--foreground))]">{likelyOrigin}</p>
          </div>
        )}
      </div>

      <Bars score={score} />

      {summary && (
        <div>
          <p className="text-xs uppercase tracking-wide text-[rgb(var(--foreground-tertiary))] mb-1">
            Summary
          </p>
          <p className="text-sm text-[rgb(var(--foreground-secondary))]">{summary}</p>
        </div>
      )}

      {issues.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-wide text-[rgb(var(--foreground-tertiary))] mb-2">
            Concrete issues
          </p>
          <ul className="space-y-1.5 text-sm text-[rgb(var(--foreground-secondary))]">
            {issues.map((it, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-amber-300">•</span>
                <span>{it}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* The conversion hook */}
      <div className="rounded-xl border border-[rgb(var(--primary))]/40 bg-[rgb(var(--primary))]/[0.08] p-4">
        <p className="text-sm font-medium text-[rgb(var(--foreground))]">
          {score.overall < 75
            ? `This transcript scores ${score.overall}/100${likelyOrigin && likelyOrigin !== 'Unknown' ? `, typical of ${likelyOrigin}` : ''}. Re-transcribing the source audio with Wisprs typically lifts formatting + speaker handling by 15–25 points.`
            : `Solid transcript (${score.overall}/100). Want this kind of output without the price tag?`}
        </p>
        <Link
          // The score and likely-origin used to ride along as URL params next to
          // utm_*. They are real signal, so they moved into the event rather than
          // being dropped: internal links must not carry UTMs, which would
          // overwrite the visitor's actual acquisition source.
          href="/sign-up"
          onClick={() =>
            trackFreeToolCtaClick({
              surface: 'tool_page',
              slug: 'transcript-quality-analyzer',
              destination: '/sign-up',
              label: `result signup (score ${score.overall}${
                likelyOrigin && likelyOrigin !== 'Unknown' ? `, ${likelyOrigin}` : ''
              })`.slice(0, 64),
            })
          }
          prefetch={false}
          className={cn(GRADIENT_CTA_CLASS, 'mt-3 px-4 py-2 text-xs')}
        >
          Re-transcribe with Wisprs · free →
        </Link>
      </div>
    </Card>
  );
}

function Bars({ score }: { score: AnalyzerScore }) {
  const rows: { label: string; key: keyof AnalyzerScore }[] = [
    { label: 'Formatting', key: 'formatting' },
    { label: 'Clarity', key: 'clarity' },
    { label: 'Completeness', key: 'completeness' },
    { label: 'Speaker handling', key: 'speakerHandling' },
  ];

  return (
    <div className="space-y-2.5">
      {rows.map(({ label, key }) => {
        const v = score[key];

        return (
          <div key={key}>
            <div className="mb-1 flex items-center justify-between text-xs">
              <span className="text-[rgb(var(--foreground-secondary))]">{label}</span>
              <span className="font-semibold text-[rgb(var(--foreground))]">{v}/100</span>
            </div>
            <div className="h-1.5 w-full rounded-full bg-[rgb(var(--surface-hover))] overflow-hidden">
              <div
                className={`h-full rounded-full ${
                  v >= 80 ? 'bg-emerald-400' : v >= 60 ? 'bg-amber-400' : 'bg-red-400'
                }`}
                style={{ width: `${v}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
